import { Component, computed, effect, EventEmitter, HostBinding, Input, Output, signal } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { AgMediaUploadModel, DEFAULT_MAX_MEDIA_UPLOAD_SIZE_MB } from '../ag-media-uploader-modal.models';
import { AgMediaUploaderModalService, FileData } from '../ag-media-uploader-modal.service';
import { ValueChangedEvent } from 'devextreme/ui/text_box';
import { GetImageContentByUrlResponse } from '@ag-common-lib/public-api';
import { CloudFunctionsService } from '../../../services/cloud-functions.service';
import {
  base64WithoutPrefix,
  buildUIBase64,
  fileToUIBase64,
} from '../ag-media-uploader-modal.utils';
import { ToastrService } from 'ngx-toastr';
import {
  downscaleImageToFit,
  loadImageFromBase64,
} from 'ag-common-svc/lib/components/ag-media-uploader/utils/downscale-image-to-fit.util';

@Component({
  selector: 'ag-shr-upload-from-url-tab',
  templateUrl: './upload-from-url-tab.component.html',
  styleUrls: ['./upload-from-url-tab.component.scss'],
})
export class UploadFromUrlTabComponent {
  @HostBinding('class') className = 'upload-from-disk-tab';
  imageUrlBlob: string | null = null;

  isLoadingSig = signal(false);
  isSuccessSig = signal(false);
  isValidSig = signal(true);

  urlSig = signal<string>('');

  protected imagePreviewUrl: string;

  private _response: GetImageContentByUrlResponse;
  private _debounceTimer: any;

  @Input() maxFileSizeMb = DEFAULT_MAX_MEDIA_UPLOAD_SIZE_MB;
  @Input() applyImageDownscale = false;
  @Input()
  set isSplitLayout(value: boolean) {
    this.className = value ? 'upload-from-disk-tab upload-from-disk-tab--split' : 'upload-from-disk-tab';
  }

  @Output() mediaUrlChange = new EventEmitter<AgMediaUploadModel | null>();

  _isImageValid$ = new BehaviorSubject(true);

  debouncedUrl = computed(async () => {
    const value = this.urlSig();

    // debounce
    clearTimeout(this._debounceTimer);

    return new Promise<string>(resolve => {
      this._debounceTimer = setTimeout(() => resolve(value), 1000);
    });
  });

  constructor(
    private agMediaUploaderService: AgMediaUploaderModalService,
    private toastrService: ToastrService,
    private cloudFunctionsService: CloudFunctionsService,
  ) {
    effect(async () => {
      const url = await this.debouncedUrl();
      this.imageUrlToBase64({ value: url } as any);
    });

    effect(
      () => {
        const value = this.urlSig();
        if (!value) {
          this.clear();
        }
      },
      { allowSignalWrites: true },
    );
  }

  handleIsImageValid = async isValid => {
    this._isImageValid$.next(isValid);
    this.isValidSig.set(isValid);

    if (isValid) {
      const imageBase64FileSize = this.getFileSizeFromBase64InBytes(this._response?.base64);
      const imageData = Object.assign({}, this._response as Partial<FileData>, {
        fileName: '',
        fileSize: imageBase64FileSize,
      }) as FileData;
      this.agMediaUploaderService.setFile(imageData);

      return;
    }
  };

  imageUrlToBase64 = async (event: ValueChangedEvent) => {
    const url = event.value;

    this.isSuccessSig.set(false);
    this.isLoadingSig.set(true);
    this.isValidSig.set(true);

    if (!url) {
      this.isLoadingSig.set(false);
      this.agMediaUploaderService.setFile(null);
      return;
    }

    const response = await this.cloudFunctionsService.getImageContentByUrl({ url }).catch(() => {
      this.isLoadingSig.set(false);
      this.isSuccessSig.set(false);
      this.isValidSig.set(false);
      this.toastrService.error('Unable to load the image from this URL. Please check the link and try again.');
      return null;
    });

    if (!response) {
      this.agMediaUploaderService.setFile(null);
      this.imagePreviewUrl = null;
      return;
    }

    const imageBase64FileSize = this.getFileSizeFromBase64InBytes(response?.base64);
    if (imageBase64FileSize > this.maxFileSizeMb * 1024 * 1024) {
      if (!this.applyImageDownscale) {
        this.validateSize(imageBase64FileSize);
        this.isLoadingSig.set(false);
        this.isSuccessSig.set(false);
        this.isValidSig.set(false);
        this.agMediaUploaderService.setFile(null);
        this.imagePreviewUrl = null;
        this._response = null;
        return;
      }

      const downscaled = await this.downscaleResponse(response);
      if (!downscaled) {
        this.isLoadingSig.set(false);
        this.isSuccessSig.set(false);
        this.isValidSig.set(false);
        this.agMediaUploaderService.setFile(null);
        this.imagePreviewUrl = null;
        this._response = null;
        return;
      }

      this._response = downscaled;
      this.imagePreviewUrl = buildUIBase64(downscaled.base64, downscaled.contentType);
      this.isSuccessSig.set(true);
      this.isLoadingSig.set(false);
      return;
    }

    this._response = response;
    this.imagePreviewUrl = buildUIBase64(response?.base64, response?.contentType);

    this.isSuccessSig.set(true);
    this.isLoadingSig.set(false);
  };

  clear() {
    this.imagePreviewUrl = null;
    this.isSuccessSig.set(false);
    this.isLoadingSig.set(false);
    this.isValidSig.set(true);
    this._response = null;
  }

  reset() {
    this.urlSig.set('');
    this.clear();
  }

  private downscaleResponse = async (
    response: GetImageContentByUrlResponse,
  ): Promise<GetImageContentByUrlResponse | null> => {
    const maxBytes = this.maxFileSizeMb * 1024 * 1024;

    try {
      const image = await loadImageFromBase64(response.base64, response.contentType);
      const result = await downscaleImageToFit(image, {
        maxBytes,
        forceJpeg: true,
        sourceType: response.contentType,
        fileName: 'image-from-url',
      });

      if (result.file.size > maxBytes) {
        this.toastrService.error(
          `Unable to downscale image under ${this.getMaxFileSizeLabel()}. Try a smaller image.`,
        );
        return null;
      }

      const dataUrl = await fileToUIBase64(result.file);
      return {
        ...response,
        base64: base64WithoutPrefix(dataUrl),
        contentType: result.contentType,
      };
    } catch (error) {
      this.toastrService.error(
        `File is too big! Max allowed size is ${this.getMaxFileSizeLabel()}. Image 1920 px width or height is recommended.`,
      );
      return null;
    }
  };

  private getFileSizeFromBase64InBytes = (base64String: string): number => {
    if (!base64String?.length) {
      return 0;
    }

    return base64String.length * (3 / 4);
  };

  private validateSize(fileSize: number): boolean {
    const maxAllowedSize = this.maxFileSizeMb * 1024 * 1024;
    if (fileSize > maxAllowedSize) {
      this.toastrService.error(
        `File is too big! Max allowed size is ${this.getMaxFileSizeLabel()}. Image 1920 px width or height is recommended.`,
      );
      return false;
    }
    return true;
  }

  private getMaxFileSizeLabel(): string {
    if (this.maxFileSizeMb < 1) {
      return `${Math.round(this.maxFileSizeMb * 1024)}KB`;
    }

    return `${this.maxFileSizeMb}MB`;
  }
}
